import React, { useState, useEffect, useCallback } from 'react';
import { inboxFetch } from '../lib/inboxApi';
import { colors } from '../theme/tokens';

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
}

function formatCountdown(ms) {
  if (ms <= 0) return null;
  const totalMins = Math.floor(ms / 60000);
  const h = Math.floor(totalMins / 60);
  const m = totalMins % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

export default function InboxNotifications({ agentEmail, agentName }) {
  const [subscribed, setSubscribed] = useState(false);
  const [permissionState, setPermissionState] = useState('default');
  const [prefs, setPrefs] = useState({
    workHoursOnly: false,
    workStart: '08:00',
    workEnd: '17:00',
    workDays: [1, 2, 3, 4, 5],
    pausedUntil: null
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);
  const [countdown, setCountdown] = useState(null);

  // Check existing subscription and load prefs
  useEffect(() => {
    async function init() {
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        setLoading(false);
        return;
      }
      setPermissionState(Notification.permission);

      try {
        // Use getRegistration instead of .ready to avoid hanging indefinitely
        const reg = await Promise.race([
          navigator.serviceWorker.getRegistration('/sw.js'),
          new Promise((resolve) => setTimeout(() => resolve(null), 3000))
        ]);
        if (reg) {
          const existing = await reg.pushManager.getSubscription();
          setSubscribed(!!existing);
        }
      } catch {
        // non-fatal — user can still click Enable
      }

      if (agentEmail) {
        try {
          const res = await inboxFetch(`/api/push/preferences?email=${encodeURIComponent(agentEmail)}`);
          if (res.ok) {
            const data = await res.json();
            if (data) setPrefs((p) => ({ ...p, ...data }));
          }
        } catch {
          // non-fatal
        }
      }
      setLoading(false);
    }
    init();
  }, [agentEmail]);

  // Countdown ticker
  useEffect(() => {
    if (!prefs.pausedUntil) { setCountdown(null); return; }
    const tick = () => {
      const ms = new Date(prefs.pausedUntil) - Date.now();
      setCountdown(ms > 0 ? formatCountdown(ms) : null);
      if (ms <= 0) setPrefs((p) => ({ ...p, pausedUntil: null }));
    };
    tick();
    const id = setInterval(tick, 30000);
    return () => clearInterval(id);
  }, [prefs.pausedUntil]);

  const savePrefs = useCallback(async (updatedPrefs) => {
    if (!agentEmail) return;
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      await inboxFetch(`/api/push/preferences`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: agentEmail, preferences: updatedPrefs })
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch {
      setError('Could not save preferences.');
    } finally {
      setSaving(false);
    }
  }, [agentEmail]);

  const handleSubscribe = async () => {
    setError(null);
    try {
      const permission = await Notification.requestPermission();
      setPermissionState(permission);
      if (permission !== 'granted') {
        setError('Permission denied. Please allow notifications in your browser settings.');
        return;
      }
      const res = await inboxFetch(`/api/push/vapid-public-key`);
      const { publicKey } = await res.json();
      const reg = await navigator.serviceWorker.ready;
      const subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey)
      });
      await inboxFetch(`/api/push/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subscription, agentEmail, agentName })
      });
      setSubscribed(true);
    } catch (err) {
      setError('Could not enable notifications: ' + err.message);
    }
  };

  const handleUnsubscribe = async () => {
    setError(null);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await inboxFetch(`/api/push/subscribe`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ endpoint: sub.endpoint })
        });
        await sub.unsubscribe();
      }
      setSubscribed(false);
    } catch (err) {
      setError('Could not disable notifications: ' + err.message);
    }
  };

  const updatePref = (key, value) => {
    const updated = { ...prefs, [key]: value };
    setPrefs(updated);
    savePrefs(updated);
  };

  const toggleDay = (day) => {
    const days = prefs.workDays.includes(day)
      ? prefs.workDays.filter((d) => d !== day)
      : [...prefs.workDays, day].sort((a, b) => a - b);
    updatePref('workDays', days);
  };

  const handlePause = (hours) => {
    const pausedUntil = new Date(Date.now() + hours * 3600 * 1000).toISOString();
    updatePref('pausedUntil', pausedUntil);
  };

  const handleResume = () => {
    updatePref('pausedUntil', null);
  };

  const isPushSupported = 'serviceWorker' in navigator && 'PushManager' in window;

  return (
    <div style={s.wrap}>
      <h3 style={s.sectionTitle}>Push Notifications</h3>

      {!isPushSupported && (
        <div style={s.warn}>Your browser does not support push notifications.</div>
      )}

      {isPushSupported && !loading && (
        <>
          {/* Enable / Disable */}
          <div style={s.card}>
            <div style={s.cardRow}>
              <div>
                <div style={s.label}>Browser notifications</div>
                <div style={s.hint}>
                  {subscribed ? 'You will receive OS-level notifications for new WhatsApp messages.' : 'Enable to get notified even when the tab is closed.'}
                </div>
              </div>
              <button
                style={{ ...s.pill, ...(subscribed ? s.pillOff : s.pillOn) }}
                onClick={subscribed ? handleUnsubscribe : handleSubscribe}
                disabled={permissionState === 'denied'}
              >
                {subscribed ? 'Disable' : 'Enable'}
              </button>
            </div>
            {permissionState === 'denied' && (
              <div style={s.warn}>Notifications are blocked by your browser. Allow them in site settings and reload.</div>
            )}
          </div>

          {subscribed && (
            <>
              {/* Work hours */}
              <div style={s.card}>
                <div style={s.cardRow}>
                  <div>
                    <div style={s.label}>Work hours only</div>
                    <div style={s.hint}>Only send notifications during your configured hours.</div>
                  </div>
                  <button
                    style={{ ...s.toggle, ...(prefs.workHoursOnly ? s.toggleOn : {}) }}
                    onClick={() => updatePref('workHoursOnly', !prefs.workHoursOnly)}
                  >
                    <span style={{ ...s.toggleThumb, ...(prefs.workHoursOnly ? s.toggleThumbOn : {}) }} />
                  </button>
                </div>

                {prefs.workHoursOnly && (
                  <div style={s.timeRow}>
                    <label style={s.timeLabel}>From</label>
                    <input
                      type="time"
                      style={s.timeInput}
                      value={prefs.workStart}
                      onChange={(e) => updatePref('workStart', e.target.value)}
                    />
                    <label style={s.timeLabel}>To</label>
                    <input
                      type="time"
                      style={s.timeInput}
                      value={prefs.workEnd}
                      onChange={(e) => updatePref('workEnd', e.target.value)}
                    />
                  </div>
                )}
              </div>

              {/* Active days */}
              <div style={s.card}>
                <div style={s.label}>Active days</div>
                <div style={s.hint}>Notifications are only sent on selected days.</div>
                <div style={s.daysRow}>
                  {DAY_LABELS.map((label, idx) => {
                    const active = prefs.workDays.includes(idx);
                    return (
                      <button
                        key={idx}
                        style={{ ...s.dayBtn, ...(active ? s.dayBtnActive : {}) }}
                        onClick={() => toggleDay(idx)}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Pause */}
              <div style={s.card}>
                <div style={s.label}>Pause notifications</div>
                <div style={s.hint}>Temporarily silence notifications on this device.</div>
                {countdown ? (
                  <div style={s.pausedRow}>
                    <span style={s.pausedBadge}>Paused · {countdown} remaining</span>
                    <button style={s.resumeBtn} onClick={handleResume}>Resume</button>
                  </div>
                ) : (
                  <div style={s.pauseBtns}>
                    {[1, 3, 12].map((h) => (
                      <button key={h} style={s.pauseBtn} onClick={() => handlePause(h)}>
                        Pause {h}h
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          {error && <div style={s.error}>{error}</div>}
          {saving && <div style={s.status}>Saving…</div>}
          {saved && <div style={s.statusOk}>Saved</div>}
        </>
      )}

      {loading && <div style={s.status}>Loading…</div>}
    </div>
  );
}

const s = {
  wrap: { padding: '20px', maxWidth: '480px' },
  sectionTitle: { fontSize: '15px', fontWeight: 700, color: colors.text, marginBottom: '16px', marginTop: 0 },
  card: {
    background: colors.glass,
    border: `1px solid ${colors.glassBorder}`,
    borderRadius: '10px',
    padding: '14px 16px',
    marginBottom: '12px'
  },
  cardRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' },
  label: { fontSize: '13px', fontWeight: 600, color: colors.text, marginBottom: '2px' },
  hint: { fontSize: '11px', color: colors.textMuted },
  pill: {
    padding: '6px 16px',
    borderRadius: '20px',
    border: 'none',
    fontWeight: 600,
    fontSize: '12px',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    flexShrink: 0
  },
  pillOn: { background: colors.parGreen, color: '#fff' },
  pillOff: { background: colors.red, color: '#fff' },
  toggle: {
    position: 'relative',
    width: '42px',
    height: '24px',
    borderRadius: '12px',
    background: colors.borderStrong,
    border: 'none',
    cursor: 'pointer',
    flexShrink: 0,
    transition: 'background 0.2s'
  },
  toggleOn: { background: colors.parGreen },
  toggleThumb: {
    position: 'absolute',
    top: '3px',
    left: '3px',
    width: '18px',
    height: '18px',
    borderRadius: '50%',
    background: '#fff',
    transition: 'left 0.2s'
  },
  toggleThumbOn: { left: '21px' },
  timeRow: { display: 'flex', alignItems: 'center', gap: '10px', marginTop: '12px', flexWrap: 'wrap' },
  timeLabel: { fontSize: '12px', color: colors.textMuted },
  timeInput: {
    background: colors.surfaceDeep,
    border: `1px solid ${colors.glassBorder}`,
    borderRadius: '6px',
    color: colors.text,
    padding: '5px 8px',
    fontSize: '13px'
  },
  daysRow: { display: 'flex', gap: '6px', marginTop: '10px', flexWrap: 'wrap' },
  dayBtn: {
    padding: '5px 10px',
    borderRadius: '6px',
    border: `1px solid ${colors.glassBorder}`,
    background: colors.surfaceDeep,
    color: colors.textMuted,
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer'
  },
  dayBtnActive: { background: 'rgba(47,182,92,0.20)', border: '1px solid rgba(47,182,92,0.55)', color: colors.parGreenBright },
  pauseBtns: { display: 'flex', gap: '8px', marginTop: '10px' },
  pauseBtn: {
    padding: '6px 14px',
    borderRadius: '6px',
    border: `1px solid ${colors.glassBorder}`,
    background: colors.surfaceDeep,
    color: colors.text,
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer'
  },
  pausedRow: { display: 'flex', alignItems: 'center', gap: '12px', marginTop: '10px' },
  pausedBadge: {
    background: 'rgba(240,168,48,0.16)',
    border: '1px solid rgba(240,168,48,0.45)',
    color: '#f0c68a',
    borderRadius: '6px',
    padding: '4px 10px',
    fontSize: '12px',
    fontWeight: 600
  },
  resumeBtn: {
    padding: '5px 12px',
    borderRadius: '6px',
    border: '1px solid rgba(47,182,92,0.55)',
    background: 'transparent',
    color: colors.parGreenBright,
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer'
  },
  warn: {
    background: 'rgba(240,168,48,0.14)',
    border: '1px solid rgba(240,168,48,0.45)',
    color: '#f0c68a',
    borderRadius: '8px',
    padding: '10px 12px',
    fontSize: '12px',
    marginBottom: '12px'
  },
  error: {
    color: '#f3b0aa',
    fontSize: '12px',
    marginTop: '8px'
  },
  status: { color: colors.textMuted, fontSize: '12px', marginTop: '8px' },
  statusOk: { color: colors.parGreenBright, fontSize: '12px', marginTop: '8px' }
};
